/**
 * Copiar Curso (admin). Copia o conteúdo (curso + DTP + plano de sessões) de
 * um curso de origem para um curso de destino já existente. Mantém a
 * identidade/datas do destino e NÃO copia presenças, avaliação/notas,
 * contratos, inscrições, nem o estado/ficheiros do DTP gerado.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Loader2, ArrowRight, AlertTriangle, CheckCircle2, ChevronLeft } from "lucide-react";
import { adminApi } from "../api";

const fmtDate = (v) => { if (!v) return "—"; try { return new Date(v).toLocaleDateString("pt-PT"); } catch { return v; } };

function CursoPreview({ data }) {
  if (!data) return null;
  return (
    <div className="mt-2 p-3 rounded border border-[#E6E1D6] bg-[#FAF8F2] text-sm" data-testid={`preview-${data.ID_Curso}`}>
      <div className="font-semibold text-[#0F1829]">#{data.ID_Curso} · {data.NomeCurso || data.Descricao}</div>
      <div className="text-xs text-[#6B7280] mt-1">
        Produto {data.ID_Produto} · {data.Local || "—"} · {fmtDate(data.DataInicio)} → {fmtDate(data.DataFim)}
        {" · "}{data.InCompany === "S" ? "In-company" : "Público"}
      </div>
    </div>
  );
}

export default function AdminCopiarCurso() {
  const [origem, setOrigem] = useState("");
  const [destino, setDestino] = useState("");
  const [pOrigem, setPOrigem] = useState(null);
  const [pDestino, setPDestino] = useState(null);
  const [copying, setCopying] = useState(false);
  const [done, setDone] = useState(null);

  const loadPreview = async (id, setter) => {
    setter(null);
    if (!String(id).trim()) return;
    try { setter(await adminApi.cursoCopiarPreview(id)); }
    catch (e) { setter({ erro: e.message || "Curso não encontrado" }); }
  };

  const canCopy = pOrigem && !pOrigem.erro && pDestino && !pDestino.erro && origem !== destino;

  const handleCopiar = async () => {
    if (!canCopy) return;
    if (!window.confirm(
      `Copiar o conteúdo do curso #${origem} para o curso #${destino}?\n\n` +
      `Só são preenchidos os campos VAZIOS do destino (os já preenchidos são mantidos). ` +
      `As sessões só são copiadas se o destino ainda não tiver nenhuma. ` +
      `Não afeta presenças, avaliação/notas, contratos nem inscrições, e mantém as datas do destino.`
    )) return;
    setCopying(true); setDone(null);
    try {
      const r = await adminApi.cursoCopiar(Number(origem), Number(destino), false);
      setDone(r);
      toast.success(`Copiado: ${r.campos_copiados} campos e ${r.sessoes_copiadas} sessões.`);
    } catch (e) { toast.error(e.message || "Erro ao copiar curso."); }
    finally { setCopying(false); }
  };

  const inputCls = "w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829]";
  const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold mb-1";

  return (
    <div data-testid="admin-copiar-curso" className="p-6 max-w-4xl mx-auto">
      <Link to="/admin" className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1 mb-3">
        <ChevronLeft size={12} /> Dashboard
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Copy size={20} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Copiar Curso</h1>
          <p className="text-sm text-[#4B5563]">Copia o conteúdo de um curso para outro já existente.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="p-4 bg-white border border-[#E6E1D6] rounded-md">
          <label className={labelCls}>Curso de ORIGEM (nº)</label>
          <input value={origem} onChange={(e) => setOrigem(e.target.value.replace(/\D/g, ""))}
            onBlur={() => loadPreview(origem, setPOrigem)} placeholder="Ex.: 2253" className={inputCls} data-testid="input-origem" />
          {pOrigem?.erro ? <p className="text-xs text-red-600 mt-2">{pOrigem.erro}</p> : <CursoPreview data={pOrigem} />}
        </div>
        <div className="p-4 bg-white border border-[#E6E1D6] rounded-md">
          <label className={labelCls}>Curso de DESTINO (nº) — tem de existir</label>
          <input value={destino} onChange={(e) => setDestino(e.target.value.replace(/\D/g, ""))}
            onBlur={() => loadPreview(destino, setPDestino)} placeholder="Ex.: 2260" className={inputCls} data-testid="input-destino" />
          {pDestino?.erro ? <p className="text-xs text-red-600 mt-2">{pDestino.erro}</p> : <CursoPreview data={pDestino} />}
        </div>
      </div>

      <div className="p-4 bg-amber-50 border border-amber-200 rounded-md mb-5 flex items-start gap-2.5" data-testid="copiar-aviso">
        <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <p className="text-sm text-[#6B5200] leading-relaxed">
          Copia <b>nome, área, formador, entidade, horário, plano de sessões, conteúdo pedagógico do DTP e dados de empresa</b>,
          mas só para os <b>campos vazios</b> do destino (os já preenchidos são mantidos). As <b>sessões</b> só são copiadas se o destino ainda não tiver nenhuma.
          Mantém as <b>datas e identidade</b> do destino (início/fim, edição, turma, produto, datas DTP).
          <b> Não</b> copia presenças, avaliação/notas, contratos, inscrições nem o estado/ficheiros do DTP gerado.
        </p>
      </div>

      <button onClick={handleCopiar} disabled={!canCopy || copying} data-testid="btn-copiar"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded text-white text-sm font-semibold bg-[#0F1829] hover:bg-[#1c2a45] disabled:opacity-40">
        {copying ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
        {copying ? "A copiar…" : "Copiar conteúdo"}
        {canCopy && !copying && <><span className="opacity-70">#{origem}</span><ArrowRight size={14} /><span className="opacity-70">#{destino}</span></>}
      </button>

      {done && (
        <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="copiar-resultado">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-1">
            <CheckCircle2 size={16} /> Cópia concluída
          </div>
          <p className="text-sm text-[#374151]">
            <b>{done.origem}</b> → <b>{done.destino}</b>: {done.campos_copiados} campos e {done.sessoes_copiadas} sessões copiadas.
          </p>
          <Link to={`/admin/cursos/edit?curso=${destino}`} className="inline-flex items-center gap-1 text-xs mt-2 px-2.5 py-1 rounded border border-[#0F1829] text-[#0F1829] hover:bg-[#0F1829] hover:text-white transition" data-testid="link-ver-destino">
            Ver curso destino <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  );
}
